import { VideoAsset } from '../../app';
import { CreateLikeDto } from '../../common';
import { createAppAsyncThunk } from '../../store/extensions';
import { GetMyStoriesDto, GetStoriesDto, InsertStoryDto } from './dtos';
import { StoryCategory, StoryRoom, StorySortBy } from './model';
import { ShareStoryDto } from './model/ShareStoryDto';
import { UpdateFeaturedStoryDto } from './model/UpdateFeaturedStoryDto';

const stripQueryParams = (url?: string): string | undefined => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    return parsed.origin + parsed.pathname;
  } catch {
    return url;
  }
};

import {
  deleteFeaturedContentCall,
  deleteFeaturedStoryCall,
  deleteStoryCall,
  fetchAllStoriesCall,
  fetchFollowingStoriesCall,
  fetchFullStoryCall,
  fetchMyStoriesCall,
  fetchMyStoryStatsCall,
  fetchStoriesByAuthorCall,
  fetchStoriesByTopicCall,
  fetchStoryTagsCall,
  fetchStoryTopicsCall,
  fetchSuggestedStoriesCall,
  insertFeaturedContentCall,
  insertFeaturedStoryCall,
  insertStoryCall,
  likeStoryCall,
  moderatorDeleteStoryCall,
  moderatorUpdateStoryCall,
  reportStoryCall,
  shareStoryToRoomsCall,
  updateFeaturedContentCall,
  updateFeaturedStoryCall,
  updateStoryCall,
} from './service';

export const fetchAllStories = createAppAsyncThunk(
  'stories/getAll',
  (args: {
    data?: GetStoriesDto;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchAllStoriesCall(
      args.data,
      args.xMsContinuationToken,
      args.numrecs
    );
  }
);

export const fetchFollowingStories = createAppAsyncThunk(
  'stories/getFollowing',
  (args: {
    data?: GetStoriesDto;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchFollowingStoriesCall(
      args.data,
      args.xMsContinuationToken,
      args.numrecs
    );
  }
);

export const fetchMyStories = createAppAsyncThunk(
  'stories/getMyStories',
  (args: {
    data?: GetMyStoriesDto;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchMyStoriesCall(
      args.data,
      args.xMsContinuationToken,
      args.numrecs
    );
  }
);

export const fetchStoryTags = createAppAsyncThunk('stories/getTags', () => {
  return fetchStoryTagsCall();
});

export const fetchStoryTopics = createAppAsyncThunk('stories/getTopics', () => {
  return fetchStoryTopicsCall();
});

export const fetchMyStoryStats = createAppAsyncThunk(
  'stories/getMyStoryStats',
  () => {
    return fetchMyStoryStatsCall();
  }
);

export const fetchFullStory = createAppAsyncThunk(
  'stories/getFullStory',
  (arg: { storyId: string }) => {
    return fetchFullStoryCall(arg.storyId);
  }
);

export const fetchFullStoryForEdit = createAppAsyncThunk(
  'stories/getFullStoryForEdit',
  (args: { storyId: string }) => {
    return fetchFullStoryCall(args.storyId);
  }
);

export const insertStory = createAppAsyncThunk(
  'stories/createStory',
  (args: { storyDto: InsertStoryDto; storyId?: string }) => {
    if (args.storyDto.roomToReturnTo) {
      delete args.storyDto.roomToReturnTo;
    }
    return args.storyId
      ? updateStoryCall(args.storyDto, args.storyId)
      : insertStoryCall(args.storyDto);
  }
);

export const insertFeaturedStory = createAppAsyncThunk(
  'stories/createFeaturedStory',
  (args: {
    storyTitle: string;
    storyContent: string;
    rooms: StoryRoom[];
    videos?: VideoAsset[];
  }) => {
    return insertFeaturedStoryCall({
      title: args.storyTitle,
      content: args.storyContent,
      rooms: args.rooms,
      visibility: 'private',
      allowLikes: false,
      allowComments: false,
      videos: args.videos ?? [],
    });
  }
);

export const insertFeaturedContent = createAppAsyncThunk(
  'stories/createFeaturedContent',
  (args: {
    title: string;
    content: string;
    type: string;
    dateValidFrom?: string;
    dateValidUntil?: string;
    link?: {
      url: string;
      title: string;
    };
    pictureURL?: string;
    rooms: StoryRoom[];
  }) => {
    return insertFeaturedContentCall({
      type: args.type,
      ...(args.title && { title: args.title }),
      ...(args.content && { content: args.content }),
      rooms: args.rooms,
      ...(args.dateValidFrom && { dateValidFrom: args.dateValidFrom }),
      ...(args.dateValidUntil && { dateValidUntil: args.dateValidUntil }),
      ...(args.link?.url && { link: args.link }),
      ...(args.pictureURL && { pictureURL: stripQueryParams(args.pictureURL) }),
      allowLikes: false,
      allowComments: false,
      visibility: 'private',
    });
  }
);

export const updateFeaturedContent = createAppAsyncThunk(
  'stories/updateFeaturedContent',
  (args: {
    postId: string;
    title: string;
    content: string;
    type: string;
    dateValidFrom?: string;
    dateValidUntil?: string;
    link?: {
      url: string;
      title: string;
    };
    pictureURL?: string;
    rooms: StoryRoom[];
  }) => {
    return updateFeaturedContentCall({
      id: args.postId,
      storyId: args.postId,
      type: args.type,
      ...(args.title && { title: args.title }),
      ...(args.content && { content: args.content }),
      rooms: args.rooms,
      dateValidFrom: args.dateValidFrom,
      dateValidUntil: args.dateValidUntil,
      ...(args.link?.url && { link: args.link }),
      ...(args.pictureURL && { pictureURL: stripQueryParams(args.pictureURL) }),
      allowLikes: false,
      allowComments: false,
      visibility: 'private',
    });
  }
);

export const updateFeaturedStory = createAppAsyncThunk(
  'stories/updateFeaturedStory',
  (args: { storyId: string; updateStoryDto: UpdateFeaturedStoryDto }) => {
    return updateFeaturedStoryCall(args.storyId, args.updateStoryDto);
  }
);

export const deleteFeaturedStory = createAppAsyncThunk(
  'stories/deleteFeaturedStory',
  (storyId: string) => {
    return deleteFeaturedStoryCall(storyId);
  }
);

export const deleteFeaturedContent = createAppAsyncThunk(
  'stories/deleteFeaturedContent',
  async (storyId: string) => {
    return deleteFeaturedContentCall(storyId);
  }
);

export const deleteStory = createAppAsyncThunk(
  'stories/deleteStory',
  (args: { storyId: string; isDraft?: boolean }) => {
    return deleteStoryCall(args.storyId);
  }
);

export const fetchSuggestedStories = createAppAsyncThunk(
  'stories/getSuggested',
  (args: {
    data?: GetStoriesDto;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchSuggestedStoriesCall(
      args.data,
      args.xMsContinuationToken,
      args.numrecs,
      StorySortBy.byRandom
    );
  }
);

export const reportStory = createAppAsyncThunk(
  'stories/report',
  (storyId: string) => {
    return reportStoryCall(storyId);
  }
);

export const likeStory = createAppAsyncThunk(
  'stories/likeStory',
  (args: {
    storyId: string;
    likeDto: CreateLikeDto;
    storyCategory?: StoryCategory;
  }) => {
    return likeStoryCall(args.storyId, args.likeDto);
  }
);

export const fetchStoriesByAuthor = createAppAsyncThunk(
  'stories/getStoriesByAuthor',
  (args: {
    authorId: string;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchStoriesByAuthorCall(
      args.authorId,
      args.xMsContinuationToken,
      args.numrecs
    );
  }
);

export const fetchStoriesByTopic = createAppAsyncThunk(
  'stories/getStoriesByTopic',
  (args: {
    topicName: string;
    xMsContinuationToken?: string;
    numrecs?: number;
  }) => {
    return fetchStoriesByTopicCall(
      args.topicName,
      args.xMsContinuationToken,
      args.numrecs
    );
  }
);

export const moderatorUpdateStory = createAppAsyncThunk(
  'stories/moderator/updateStory',
  (args: {
    storyDto: InsertStoryDto;
    storyId?: string;
    moderationReason?: string;
  }) => {
    return moderatorUpdateStoryCall(
      args.storyDto,
      args.storyId,
      args.moderationReason
    );
  }
);

export const moderatorDeleteStory = createAppAsyncThunk(
  'stories/moderator/deleteStory',
  (args: { storyId: string }) => {
    return moderatorDeleteStoryCall(args.storyId);
  }
);

export const shareStoryToRooms = createAppAsyncThunk(
  'stories/share',
  (args: { storyId: string; shareStoryDto: ShareStoryDto }) => {
    return shareStoryToRoomsCall(args.storyId, args.shareStoryDto);
  }
);
